import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { NotificationType } from '@/store/notifications';
import { useAuthStore } from '@/store/auth';
import {
  clearAllNotifications,
  deleteNotification,
  fetchUserNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type ApiUserNotification,
} from '@/services/userNotifications';

export interface UserNotification {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
  createdAt: number;
  read: boolean;
}

const MIN_SYNC_INTERVAL_MS = 8_000;

function mapApiNotification(item: ApiUserNotification): UserNotification {
  const raw = item as ApiUserNotification & { is_read?: boolean };
  return {
    id: item.id,
    type: item.type,
    title: item.title,
    message: item.message,
    createdAt: new Date(item.created_at).getTime(),
    read: Boolean(raw.read ?? raw.is_read),
  };
}

export const useUserNotificationsStore = defineStore('userNotifications', () => {
  const items = ref<UserNotification[]>([]);
  const activeUserId = ref<number | undefined>(undefined);
  const syncing = ref(false);
  const lastSyncError = ref<string | null>(null);

  let syncPromise: Promise<void> | null = null;
  let lastSyncAt = 0;
  /** IDs marcados localmente mientras el PATCH aún no se refleja en un sync. */
  const pendingReadIds = new Set<number>();
  const pendingDeleteIds = new Set<number>();

  const unreadCount = computed(() => items.value.filter((n) => !n.read).length);

  function resolveUserId(): number | undefined {
    const authUserId = useAuthStore().user?.id;
    return activeUserId.value ?? authUserId;
  }

  function reset() {
    items.value = [];
    lastSyncError.value = null;
    lastSyncAt = 0;
    syncPromise = null;
    pendingReadIds.clear();
    pendingDeleteIds.clear();
  }

  function applyServerItems(serverItems: ApiUserNotification[]) {
    items.value = serverItems
      .filter((item) => !pendingDeleteIds.has(item.id))
      .map((item) => {
        const mapped = mapApiNotification(item);
        if (pendingReadIds.has(mapped.id)) {
          mapped.read = true;
        }
        return mapped;
      });
  }

  async function syncFromServer(options: { force?: boolean } = {}) {
    const userId = resolveUserId();
    if (!userId) {
      reset();
      return;
    }

    const now = Date.now();
    if (
      !options.force
      && syncPromise === null
      && now - lastSyncAt < MIN_SYNC_INTERVAL_MS
      && items.value.length > 0
    ) {
      return;
    }

    if (syncPromise) {
      return syncPromise;
    }

    activeUserId.value = userId;
    syncing.value = true;
    lastSyncError.value = null;

    syncPromise = (async () => {
      try {
        const response = await fetchUserNotifications();
        applyServerItems(response.items);
        lastSyncAt = Date.now();
      } catch (err: unknown) {
        const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        lastSyncError.value = message || 'No se pudieron cargar las notificaciones.';
      } finally {
        syncing.value = false;
        syncPromise = null;
      }
    })();

    return syncPromise;
  }

  function hydrate(userId?: number) {
    activeUserId.value = userId;
    if (!userId) {
      reset();
      return;
    }
    void syncFromServer({ force: true });
  }

  /** @deprecated El servidor crea la bienvenida; usar syncFromServer */
  function seedWelcomeIfEmpty(_title: string, _message: string) {
    // noop — las notificaciones vienen del API
  }

  async function markAsRead(id: number) {
    const item = items.value.find((n) => n.id === id);
    if (!item || item.read) return;

    item.read = true;
    pendingReadIds.add(id);
    try {
      await markNotificationRead(id);
      pendingReadIds.delete(id);
    } catch {
      pendingReadIds.delete(id);
      item.read = false;
      await syncFromServer({ force: true });
    }
  }

  async function markAllAsRead() {
    const unread = items.value.filter((n) => !n.read);
    if (unread.length === 0) return;

    for (const item of unread) {
      item.read = true;
      pendingReadIds.add(item.id);
    }

    try {
      await markAllNotificationsRead();
      for (const item of unread) {
        pendingReadIds.delete(item.id);
      }
    } catch {
      for (const item of unread) {
        pendingReadIds.delete(item.id);
      }
      await syncFromServer({ force: true });
    }
  }

  async function remove(id: number) {
    const idx = items.value.findIndex((n) => n.id === id);
    if (idx === -1) return;

    const [removed] = items.value.splice(idx, 1);
    pendingDeleteIds.add(id);
    pendingReadIds.delete(id);
    try {
      await deleteNotification(id);
      pendingDeleteIds.delete(id);
    } catch {
      pendingDeleteIds.delete(id);
      items.value.splice(idx, 0, removed);
      await syncFromServer({ force: true });
    }
  }

  async function clearAll() {
    if (items.value.length === 0) return;

    const backup = [...items.value];
    items.value = [];
    pendingReadIds.clear();
    pendingDeleteIds.clear();

    try {
      await clearAllNotifications();
    } catch {
      items.value = backup;
      await syncFromServer({ force: true });
    }
  }

  return {
    items,
    syncing,
    lastSyncError,
    unreadCount,
    hydrate,
    syncFromServer,
    seedWelcomeIfEmpty,
    markAsRead,
    markAllAsRead,
    remove,
    clearAll,
  };
});
